// @ts-check
/**
 * Jonny Bee's — Entry-Animation.
 *
 * Elemente auf Startseite + Kollektion + Produktseite gleiten beim Eintritt in
 * den Viewport herein und blenden ein, gestaffelt innerhalb einer Section (aus
 * jonnybee.at / Elementor, gemessen 10.09.2026). Produktseiten-Galerie: nur
 * einblenden, kein Versatz.
 *
 * FOUC-Schutz: der Versteck-Zustand im CSS gilt nur unter `html.jb-anim`. Diese
 * Klasse setzt das Inline-Script in snippets/jonnybee-entry-animation.liquid VOR
 * dem ersten Paint (mit 2,5s-Guard). Dieses Skript bestellt den Guard ab und
 * übernimmt; bei jedem Ausstieg wird `html.jb-anim` entfernt → alles sichtbar.
 *
 * CSS + @keyframes: assets/jonnybee-base.css, Abschnitt „Entry-Animation".
 */
(function () {
  'use strict';

  var html = document.documentElement;

  function unhide() {
    html.classList.remove('jb-anim');
  }

  try {
    // Guard aus dem Inline-Script abbestellen — ab hier steuern wir.
    var w = /** @type {{ __jbAnimGuard?: number }} */ (/** @type {unknown} */ (window));
    if (w.__jbAnimGuard) {
      window.clearTimeout(w.__jbAnimGuard);
    }

    var reduce = false;
    try {
      reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      reduce = false;
    }
    if (reduce || !('IntersectionObserver' in window)) {
      unhide();
      return;
    }

    var main = document.getElementById('MainContent');
    var tpl = main ? main.getAttribute('data-template') || '' : '';
    var isIndex = tpl.indexOf('index') === 0;
    var isCollection = tpl.indexOf('collection') === 0;
    var isProduct = tpl.indexOf('product') === 0;
    if (!main || (!isIndex && !isCollection && !isProduct)) {
      unhide();
      return;
    }

    /** Staffel-Schritt in ms (Token `--jb-anim-step`; unter 750px = 0). */
    var step = parseFloat(getComputedStyle(html).getPropertyValue('--jb-anim-step')) || 0;

    /**
     * @param {Element} el
     * @param {number} delay ms
     * @param {'up' | 'down' | 'fade' | 'fade-down'} variant
     */
    function reveal(el, delay, variant) {
      window.setTimeout(function () {
        if (variant === 'down') {
          el.classList.add('jb-anim--down');
        } else if (variant === 'fade') {
          el.classList.add('jb-anim--fade');
        } else if (variant === 'fade-down') {
          el.classList.add('jb-anim--fade-down');
        }
        el.classList.add('jb-in');
      }, delay);
    }

    /**
     * @typedef {'up' | 'down' | 'fade' | 'fade-down'} Variant
     * @typedef {{ kids: string | null, variant: Variant, media: Variant }} GroupConfig
     */

    /** @type {WeakMap<Element, GroupConfig>} */
    var groupConfig = new WeakMap();
    /** @type {WeakSet<Element>} */
    var groupSeen = new WeakSet();

    var groupObserver = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) {
            return;
          }
          var target = entry.target;
          var cfg = groupConfig.get(target);
          if (!cfg) {
            return;
          }
          /** @type {Element[]} */
          var kids = cfg.kids
            ? Array.prototype.slice.call(target.querySelectorAll(cfg.kids))
            : [target];
          var i = 0;
          kids.forEach(function (kid) {
            if (kid.classList.contains('jb-in')) {
              return;
            }
            var custom = kid.getAttribute('data-jb-anim-delay');
            // Bilder/Medien: sanft einblenden statt um die eigene Höhe herein-
            // zufliegen (wirkt sonst unruhig) + kein Stagger. Welche Variante
            // legt die Gruppe fest: `fade` (PDP-Galerie) oder `fade-down`
            // (Hero-Bild: von oben herein).
            var isMedia =
              /(^|[\s_-])(image|media|img)/i.test(kid.className) || kid.tagName === 'IMG';
            var index = isMedia ? 0 : i++;
            var variant = isMedia ? cfg.media : cfg.variant;
            var delay = custom != null ? parseFloat(custom) || 0 : Math.min(index, 4) * step;
            reveal(kid, delay, variant);
          });
          obs.unobserve(target);
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );

    /**
     * @param {string} selector
     * @param {string | null} kids
     * @param {Variant} variant
     * @param {Variant} [media] Variante für Bild-/Medien-Kinder (Default `fade`).
     */
    function addGroup(selector, kids, variant, media) {
      if (!main) {
        return;
      }
      Array.prototype.forEach.call(
        main.querySelectorAll(selector),
        function (/** @type {Element} */ node) {
          if (groupSeen.has(node)) {
            return;
          }
          groupSeen.add(node);
          groupConfig.set(node, { kids: kids, variant: variant, media: media || 'fade' });
          groupObserver.observe(node);
        }
      );
    }

    // --- Produktkacheln: eigener Observer, pro Sicht-Batch gestaffelt ---
    var cardObserver = new IntersectionObserver(
      function (entries, obs) {
        /** @type {Element[]} */
        var batch = [];
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !entry.target.classList.contains('jb-in')) {
            batch.push(entry.target);
          }
        });
        batch.sort(function (a, b) {
          return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
        });
        batch.forEach(function (card, index) {
          reveal(card, Math.min(index, 3) * (step / 2), 'up');
          obs.unobserve(card);
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -5% 0px' }
    );

    /** @param {Element | Document} root */
    function observeCards(root) {
      Array.prototype.forEach.call(
        root.querySelectorAll('product-card'),
        function (/** @type {Element} */ card) {
          if (!card.classList.contains('jb-in')) {
            cardObserver.observe(card);
          }
        }
      );
    }

    if (isIndex) {
      addGroup('.hero-wrapper', '.group-block-content > *', 'down', 'fade-down');
      addGroup('.section-resource-list__header', '.group-block-content > *', 'up');
      addGroup('.section-content-wrapper', ':scope > *', 'up');
    }
    if (isCollection) {
      addGroup('.facets-block-wrapper', null, 'up');
    }
    if (isProduct) {
      // Detailspalte: Blätter der (verschachtelten) Gruppen, beim Laden gestaffelt.
      addGroup(
        '.product-details',
        '.group-block-content > :not(.group-block):not(.view-product-title)',
        'up'
      );
      // Medien-Galerie als eine Einheit — nur einblenden, kein Versatz.
      addGroup('.product-information__media', null, 'fade');
      // .product-recommendations bewusst NICHT animiert (weder Überschrift noch
      // Kacheln-Gruppe) — der Block liegt unter <product-recommendations
      // data-hydration-key>, dessen Nachladen per morphSection() genau diesen
      // Teilbaum ersetzt. Nur die Kacheln selbst laufen über den Karten-
      // Observer unten, der <product-card> unabhängig vom Eltern-Knoten fängt.
    }

    observeCards(main);

    // Nachträglich eingefügte Kacheln abfangen: Kollektion mit
    // enable_infinite_scroll, PDP-Empfehlungen (morphSection). Beobachtet nur
    // <product-card>-Elemente direkt — unabhängig davon, ob ihr Eltern-Knoten
    // beim Morphen ersetzt wird.
    if (isCollection || isProduct) {
      var mo = new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          Array.prototype.forEach.call(m.addedNodes, function (node) {
            if (!node || node.nodeType !== 1) {
              return;
            }
            var el = /** @type {Element} */ (node);
            if (el.matches && el.matches('product-card')) {
              if (!el.classList.contains('jb-in')) {
                cardObserver.observe(el);
              }
            } else if (el.querySelectorAll) {
              observeCards(el);
            }
          });
        });
      });
      mo.observe(main, { childList: true, subtree: true });
    }
  } catch (e) {
    // Irgendwas schiefgelaufen — nichts darf versteckt bleiben.
    unhide();
  }
})();
